import {useContext, useEffect, useRef, useState} from 'react';
import type {ReceivedReaction} from '@phonehq/rtc-core';
import {CallMemberDirectoryContext, ParticipantViewsContext} from './callContext';
import '../groupCalls.css';

const REACTION_LIFETIME_MS = 3200;
const MAX_VISIBLE_REACTIONS = 30;

interface FloatingReaction {
    readonly id: string;
    readonly emoji: string;
    readonly name: string;
    readonly offset: number;
}

const offsetFor = (id: string): number => {
    let hash = 0;
    for (let index = 0; index < id.length; index++) {
        hash = (hash * 31 + id.charCodeAt(index)) | 0;
    }
    return 8 + (Math.abs(hash) % 70);
};

export const ReactionsOverlay = ({subscribe}: {subscribe: (listener: (reaction: ReceivedReaction) => void) => () => void}) => {
    const views = useContext(ParticipantViewsContext);
    const directory = useContext(CallMemberDirectoryContext);
    const [reactions, setReactions] = useState<FloatingReaction[]>([]);
    const lookup = useRef({views, directory});
    lookup.current = {views, directory};

    useEffect(() => {
        const timers = new Set<ReturnType<typeof setTimeout>>();
        const unsubscribe = subscribe(reaction => {
            const view = lookup.current.views.get(reaction.from);
            const name = view ? lookup.current.directory(view.userId).name : '';
            const floating = {id: reaction.id, emoji: reaction.reaction, name, offset: offsetFor(reaction.id)};
            setReactions(current => [...current.slice(-(MAX_VISIBLE_REACTIONS - 1)), floating]);
            const timer = setTimeout(() => {
                timers.delete(timer);
                setReactions(current => current.filter(item => item.id !== floating.id));
            }, REACTION_LIFETIME_MS);
            timers.add(timer);
        });
        return () => {
            unsubscribe();
            timers.forEach(timer => clearTimeout(timer));
        };
    }, [subscribe]);

    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-live="polite" data-testid="group-call-reactions-overlay">
            {reactions.map(reaction => (
                <div
                    key={reaction.id}
                    className="phq-reaction absolute bottom-24 flex flex-col items-center"
                    style={{left: `${reaction.offset}%`}}
                >
                    <span className="text-4xl drop-shadow">{reaction.emoji}</span>
                    {reaction.name && (
                        <span className="mt-1 rounded-full bg-black/60 px-2 py-0.5 text-[11px] text-white">{reaction.name}</span>
                    )}
                </div>
            ))}
        </div>
    );
};
